import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';

import { EntityService } from '../entity/entity.service';

/** The Ops authority ladder, spelled exactly as the corpus spells it (N7). */
const OPS_TIER_LABELS: Record<string, string> = {
  ANALYST: 'Analyst',
  SENIOR_OPS: 'Senior Ops',
  CREDIT_COMMITTEE: 'Credit Committee',
};

@Injectable({ providedIn: 'root' })
export class OpsContextService {
  private readonly entityService = inject(EntityService);

  private readonly contexts = toSignal(
    this.entityService.getContexts().pipe(map((res) => res.contexts)),
    { initialValue: null },
  );

  /** True when the user has a system context with an ops_tier assigned. */
  readonly isOps = () => {
    const ctxs = this.contexts();
    if (!ctxs) {
      return false;
    }
    return ctxs.some((c) => c.type === 'system' && c.ops_tier != null);
  };

  /** The ops tier of the system context, or null. */
  readonly opsTier = () => {
    const ctxs = this.contexts();
    if (!ctxs) {
      return null;
    }
    return ctxs.find((c) => c.type === 'system' && c.ops_tier != null)?.ops_tier ?? null;
  };

  /**
   * The acting tier as its exact CapX authority label (N7 — never "Manager",
   * "Staff" or "Admin"). Lives here rather than in a component so every Ops
   * surface spells the ladder the same way.
   */
  readonly opsTierLabel = () => {
    const tier = this.opsTier();
    return tier ? (OPS_TIER_LABELS[tier] ?? tier) : null;
  };

  /**
   * True when the logged-in Ops user sits on the Credit Committee and may cast
   * a resolution approval (orthogonal to the acting tier — the "dual hat").
   * Server-side enforcement still applies; this only gates the Approve action.
   */
  readonly isCcMember = () => {
    const ctxs = this.contexts();
    if (!ctxs) {
      return false;
    }
    return ctxs.some((c) => c.type === 'system' && c.is_credit_committee_member === true);
  };
}
